// Markdown ↔ PM doc round-trip for the notes editor.
//
// Deliberately a tight subset. Only what input rules can produce
// stays round-trippable:
//   - paragraphs (blank line = separator)
//   - ATX headings `#`, `##`, `###`
//   - bullet lists (`- ` or `* `)
//   - ordered lists (`1. `, renumbered on serialize so writers can
//     reorder freely without renumbering by hand)
//   - blockquotes (`> `)
//   - fenced code blocks (``` … ```)
//   - horizontal rules (`---` or `***`)
//   - inline: **strong**, *em*/_em_, `code`, [text](href) links
//
// Nested lists and tables are deliberately not supported by the
// parser — they're not produced by any input rule, and round-tripping
// them through this serializer would lose information. A writer who
// pastes nested markdown in will see a flattened version on next
// save; not great, but acceptable for a scratchpad.

import { notesSchema as S } from './schema.js';

// =========================================================================
// Parse: markdown text → PM doc
// =========================================================================

export function markdownToDoc(text) {
  const blocks = parseBlocks(typeof text === 'string' ? text : '');
  const nodes = blocks.map(blockToNode).filter(Boolean);
  if (nodes.length === 0) nodes.push(S.nodes.paragraph.create());
  return S.nodes.doc.create(null, nodes);
}

function parseBlocks(src) {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — just advance, blocks are split by blank-line gaps
    // implicitly via "is this still the same block type?" below.
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Fenced code: ```lang\n…lines…\n```
    const fence = /^```\s*([A-Za-z0-9_-]*)\s*$/.exec(line);
    if (fence) {
      const codeLines = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // closing fence
      blocks.push({ type: 'code_block', text: codeLines.join('\n') });
      continue;
    }

    // Horizontal rule: three or more `-` or `*` on their own.
    if (/^(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    // ATX heading: 1–3 `#` followed by space and text. Beyond h3
    // collapses to h3 — keeps the schema small.
    const heading = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length, 3);
      blocks.push({ type: 'heading', level, text: heading[2] });
      i++;
      continue;
    }

    // Blockquote: contiguous `> …` lines.
    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      // Parse the inner text recursively so a blockquote can contain
      // paragraphs / lists / sub-quotes.
      blocks.push({ type: 'blockquote', children: parseBlocks(quoteLines.join('\n')) });
      continue;
    }

    // Bullet list: a contiguous run of `- ` / `* ` lines.
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        const text = lines[i].replace(/^[-*]\s+/, '');
        items.push(text);
        i++;
      }
      blocks.push({ type: 'bullet_list', items });
      continue;
    }

    // Ordered list: `1.`, `2.`, … We don't enforce the numbers — they
    // get renumbered on serialize anyway.
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      let order = null;
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        const m = /^(\d+)\.\s+(.*)$/.exec(lines[i]);
        if (m) {
          if (order === null) order = Number(m[1]);
          items.push(m[2]);
        }
        i++;
      }
      blocks.push({ type: 'ordered_list', order: order || 1, items });
      continue;
    }

    // Paragraph: gather contiguous non-blank, non-block lines.
    const paraLines = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !startsBlock(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
  }

  return blocks;
}

function startsBlock(line) {
  return (
    /^#{1,6}\s+/.test(line) ||
    /^[-*]\s+/.test(line) ||
    /^\d+\.\s+/.test(line) ||
    /^>\s?/.test(line) ||
    /^```/.test(line) ||
    /^(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())
  );
}

function blockToNode(block) {
  switch (block.type) {
    case 'paragraph':
      return S.nodes.paragraph.create(null, inlineNodes(block.text));
    case 'heading':
      return S.nodes.heading.create({ level: block.level }, inlineNodes(block.text));
    case 'hr':
      return S.nodes.horizontal_rule.create();
    case 'code_block':
      return S.nodes.code_block.create(null, block.text ? S.text(block.text) : []);
    case 'blockquote': {
      const inner = (block.children || []).map(blockToNode).filter(Boolean);
      if (inner.length === 0) inner.push(S.nodes.paragraph.create());
      return S.nodes.blockquote.create(null, inner);
    }
    case 'bullet_list': {
      const items = block.items.map((t) =>
        S.nodes.list_item.create(null, S.nodes.paragraph.create(null, inlineNodes(t))),
      );
      return S.nodes.bullet_list.create(null, items);
    }
    case 'ordered_list': {
      const items = block.items.map((t) =>
        S.nodes.list_item.create(null, S.nodes.paragraph.create(null, inlineNodes(t))),
      );
      return S.nodes.ordered_list.create({ order: block.order }, items);
    }
    default:
      return null;
  }
}

// -------- inline tokenizer --------
//
// One-pass, left-to-right. Supports nesting of strong/em (one level —
// `**_X_**` works, but `**X_Y_Z**` won't try to mix). Code spans
// suppress further parsing inside, matching CommonMark.

function inlineNodes(text) {
  if (!text) return [];
  return parseInline(text, S.marks);
}

function parseInline(text, marks, activeMarks) {
  const out = [];
  let i = 0;
  let buf = '';
  const active = activeMarks || [];

  const pushText = () => {
    if (buf.length > 0) {
      out.push(S.text(buf, active.length ? active : null));
      buf = '';
    }
  };

  while (i < text.length) {
    const c = text[i];

    // Code span — earliest-match because backticks suppress everything
    // until the matching backtick run length.
    if (c === '`') {
      const ticks = countRun(text, i, '`');
      const closeIdx = findCloseTicks(text, i + ticks, ticks);
      if (closeIdx !== -1) {
        pushText();
        const inner = text.slice(i + ticks, closeIdx).trim();
        const codeMark = marks.code.create();
        out.push(S.text(inner, [...active, codeMark]));
        i = closeIdx + ticks;
        continue;
      }
    }

    // Strong: ** … ** or __ … __
    if ((c === '*' || c === '_') && text[i + 1] === c) {
      const marker = c + c;
      const close = findCloseMarker(text, i + 2, marker);
      if (close !== -1) {
        pushText();
        const inner = text.slice(i + 2, close);
        const strongMark = marks.strong.create();
        out.push(...parseInline(inner, marks, [...active, strongMark]));
        i = close + 2;
        continue;
      }
    }

    // Em: * … * or _ … _
    if ((c === '*' || c === '_') && text[i + 1] !== c) {
      const close = findCloseMarker(text, i + 1, c);
      if (close !== -1 && close > i + 1) {
        pushText();
        const inner = text.slice(i + 1, close);
        const emMark = marks.em.create();
        out.push(...parseInline(inner, marks, [...active, emMark]));
        i = close + 1;
        continue;
      }
    }

    // Link: [text](href)
    if (c === '[') {
      const linkMatch = matchLink(text, i);
      if (linkMatch) {
        pushText();
        const linkMark = marks.link.create({ href: linkMatch.href });
        out.push(...parseInline(linkMatch.text, marks, [...active, linkMark]));
        i = linkMatch.end;
        continue;
      }
    }

    buf += c;
    i++;
  }
  pushText();
  return out;
}

function countRun(text, from, ch) {
  let n = 0;
  while (text[from + n] === ch) n++;
  return n;
}

function findCloseTicks(text, from, count) {
  let i = from;
  while (i < text.length) {
    if (text[i] === '`') {
      const run = countRun(text, i, '`');
      if (run === count) return i;
      i += run;
    } else {
      i++;
    }
  }
  return -1;
}

function findCloseMarker(text, from, marker) {
  // Markers don't close immediately (`**` followed by space is real
  // markdown, so require the closing marker to be non-space-preceded —
  // matches commonmark's tightness rule lightly).
  for (let i = from; i < text.length - marker.length + 1; i++) {
    if (text.substr(i, marker.length) === marker) return i;
  }
  return -1;
}

function matchLink(text, from) {
  // [text](href) — text can't contain another `[`; href can't contain `)`.
  const closeBracket = text.indexOf(']', from + 1);
  if (closeBracket === -1) return null;
  if (text[closeBracket + 1] !== '(') return null;
  const closeParen = text.indexOf(')', closeBracket + 2);
  if (closeParen === -1) return null;
  return {
    text: text.slice(from + 1, closeBracket),
    href: text.slice(closeBracket + 2, closeParen).trim(),
    end: closeParen + 1,
  };
}

// =========================================================================
// Serialize: PM doc → markdown text
// =========================================================================

export function docToMarkdown(doc) {
  const out = [];
  doc.forEach((node) => {
    out.push(blockToMd(node));
  });
  return out
    .filter((s) => s !== null)
    .join('\n\n')
    .trim();
}

function blockToMd(node) {
  switch (node.type.name) {
    case 'paragraph':
      return inlineToMd(node);
    case 'heading': {
      const hashes = '#'.repeat(Math.min(node.attrs.level || 1, 3));
      return `${hashes} ${inlineToMd(node)}`;
    }
    case 'horizontal_rule':
      return '---';
    case 'code_block':
      return '```\n' + (node.textContent || '') + '\n```';
    case 'blockquote': {
      const inner = [];
      node.forEach((child) => inner.push(blockToMd(child)));
      return inner
        .join('\n\n')
        .split('\n')
        .map((l) => (l.length ? `> ${l}` : '>'))
        .join('\n');
    }
    case 'bullet_list': {
      const items = [];
      node.forEach((item) => items.push(`- ${listItemContent(item)}`));
      return items.join('\n');
    }
    case 'ordered_list': {
      const start = node.attrs.order || 1;
      const items = [];
      node.forEach((item, _o, idx) => items.push(`${start + idx}. ${listItemContent(item)}`));
      return items.join('\n');
    }
    default:
      return null;
  }
}

function listItemContent(item) {
  // List items always contain at least one paragraph. We inline the
  // first paragraph onto the bullet line and join any subsequent
  // children with a newline (rare path — input rules don't produce
  // these, but a pasted doc might).
  const lines = [];
  let first = true;
  item.forEach((child) => {
    if (first) {
      lines.push(blockToMd(child) || '');
      first = false;
    } else {
      const md = blockToMd(child);
      if (md != null) lines.push('  ' + md.replace(/\n/g, '\n  '));
    }
  });
  return lines.join('\n');
}

function inlineToMd(node) {
  let out = '';
  let activeMarks = [];

  // Wrap-marker per mark name.
  const wrap = {
    strong: '**',
    em: '*',
    code: '`',
  };

  function closeMarks(marks) {
    // Close in reverse opening order.
    for (let i = marks.length - 1; i >= 0; i--) {
      const m = marks[i];
      if (m.type.name === 'link') {
        out += `](${m.attrs.href})`;
      } else if (wrap[m.type.name]) {
        out += wrap[m.type.name];
      }
    }
  }

  function openMarks(marks) {
    for (const m of marks) {
      if (m.type.name === 'link') {
        out += '[';
      } else if (wrap[m.type.name]) {
        out += wrap[m.type.name];
      }
    }
  }

  node.forEach((child) => {
    if (child.isText) {
      const childMarks = child.marks || [];
      // Close marks not in the child's set.
      const stillOpen = [];
      const toClose = [];
      for (const m of activeMarks) {
        if (childMarks.some((cm) => cm.eq(m))) stillOpen.push(m);
        else toClose.push(m);
      }
      // Close in reverse order — but only those that aren't shared.
      if (toClose.length > 0) {
        // To keep wrappers properly nested, close everything from the
        // first divergence point onward.
        const firstDivergeIdx = activeMarks.findIndex((m) => toClose.includes(m));
        const closing = activeMarks.slice(firstDivergeIdx);
        closeMarks(closing);
        activeMarks = activeMarks.slice(0, firstDivergeIdx);
      }
      // Open any new marks present on the child but not active yet.
      const toOpen = childMarks.filter((m) => !activeMarks.some((am) => am.eq(m)));
      openMarks(toOpen);
      activeMarks = [...activeMarks, ...toOpen];
      out += child.text;
    }
  });
  // Close any remaining open marks at the end of the block.
  closeMarks(activeMarks);
  return out;
}
